import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { makeApiRequest } from '../../../utils/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Box, Grid, Typography, TextField, Button, Alert, Card, CardContent,
    FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput,
    FormControlLabel, Divider, CircularProgress
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const assessmentOptions = [
    { label: 'Domain Technical concept', generic: 'DOMAIN TECHNICAL CONCEPT' },
    { label: 'Drafting', generic: 'DRAFTING' },
    { label: 'Modeling', generic: 'MODELING' },
    { label: 'Attendance', generic: 'ATTENDANCE' },
    { label: 'Project', generic: 'PROJECT (DRAFTING + MODELING)' }
];

const EditCourse = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const courseId = searchParams.get('courseId');

    const [courseName, setCourseName] = useState('');
    const [modules, setModules] = useState([]);
    const [selectedModuleIds, setSelectedModuleIds] = useState([]);
    const [selectedAssessments, setSelectedAssessments] = useState({});
    const [missingChapters, setMissingChapters] = useState([]);
    const [newChapterNames, setNewChapterNames] = useState({});
    const [loadingData, setLoadingData] = useState(true);
    const [loadingSave, setLoadingSave] = useState(false);
    const [duplicateError, setDuplicateError] = useState(null);

    useEffect(() => {
        if (!courseId) {
            toast.error('No course ID provided.');
            navigate('/course');
            return;
        }

        const fetchData = async () => {
            setLoadingData(true);
            try {
                // 1. Fetch modules
                const modulesResponse = await makeApiRequest('post', '/api/module/list', {
                    token: localStorage.getItem('authToken'),
                    per_page: 1000
                });
                const fetchedModules = modulesResponse.status === 'success' && modulesResponse.data
                    ? modulesResponse.data.data || []
                    : [];
                setModules(fetchedModules);

                // 2. Fetch course details
                const courseResponse = await makeApiRequest('post', '/api/course/list/id', {
                    token: localStorage.getItem('authToken'),
                    id: Number(courseId)
                });

                if (courseResponse && courseResponse.status === 'success' && courseResponse.data) {
                    const course = courseResponse.data;
                    setCourseName(course.course_name || '');

                    // Parse module_ids. It can be string of comma separated values or array
                    let moduleIds = [];
                    if (Array.isArray(course.module_ids)) {
                        moduleIds = course.module_ids.map(Number);
                    } else if (typeof course.module_ids === 'string') {
                        moduleIds = course.module_ids.split(',').map(Number);
                    }
                    setSelectedModuleIds(moduleIds);
                } else {
                    throw new Error('Failed to load course details.');
                }

                // 3. Fetch linked assessments
                const assessmentsResponse = await makeApiRequest('post', '/api/batch/assessments', {
                    token: localStorage.getItem('authToken'),
                    batch_id: 0,
                    course_id: Number(courseId)
                });

                if (assessmentsResponse && assessmentsResponse.status === 'success' && assessmentsResponse.data) {
                    const assessmentMap = {};
                    assessmentsResponse.data.forEach(item => {
                        assessmentMap[item.generic_name] = item.specific_name || '';
                    });
                    setSelectedAssessments(assessmentMap);
                }

            } catch (error) {
                console.error('Error fetching course edit data:', error);
                toast.error(error.message || 'Failed to load course data.');
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [courseId, navigate]);

    const clearDuplicateError = () => {
        if (duplicateError) setDuplicateError(null);
    };

    const handleModuleChange = (event) => {
        const { target: { value } } = event;
        setSelectedModuleIds(typeof value === 'string' ? value.split(',') : value);
        clearDuplicateError();
    };

    const handleAssessmentCheckboxChange = (generic) => {
        setSelectedAssessments(prev => {
            const next = { ...prev };
            if (next[generic] !== undefined) {
                delete next[generic];
            } else {
                next[generic] = '';
            }
            return next;
        });
        clearDuplicateError();
    };

    const handleOnscreenNameChange = (generic, value) => {
        setSelectedAssessments(prev => ({
            ...prev,
            [generic]: value
        }));
        clearDuplicateError();
    };

    const handleAddChapterInline = async (moduleId) => {
        const chapterName = newChapterNames[moduleId];
        if (!chapterName || !chapterName.trim()) {
            toast.warn('Please enter a chapter name');
            return;
        }

        try {
            const response = await makeApiRequest('post', '/api/chapter/add', {
                token: localStorage.getItem('authToken'),
                module_id: moduleId,
                chapter_name: chapterName,
                chapter_status: 1,
                userid: localStorage.getItem('userid') || localStorage.getItem('user_id'),
            });

            if (response.status === 'success') {
                toast.success('Chapter added successfully!');
                setMissingChapters(prev => prev.filter(item => item.module_id !== moduleId));
                setNewChapterNames(prev => {
                    const next = { ...prev };
                    delete next[moduleId];
                    return next;
                });
            } else {
                throw new Error('Failed to add chapter');
            }
        } catch (error) {
            console.error('Error adding chapter:', error);
            toast.error(error.response?.data?.message || 'Failed to add chapter');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!courseName.trim()) {
            toast.warn('Please enter a course name');
            return;
        }

        if (selectedModuleIds.length === 0) {
            toast.warn('Please select at least one module');
            return;
        }

        const assessmentKeys = Object.keys(selectedAssessments);
        if (assessmentKeys.length < 3 || assessmentKeys.length > 5) {
            toast.warn('Please select between 3 and 5 assessments');
            return;
        }

        // Validate that onscreen names are filled
        for (const generic of assessmentKeys) {
            if (!selectedAssessments[generic].trim()) {
                toast.warn(`Please enter onscreen name for ${generic}`);
                return;
            }
        }

        const payload = {
            token: localStorage.getItem('authToken'),
            course_id: Number(courseId),
            course_name: courseName,
            module_ids: selectedModuleIds,
            assessments: assessmentKeys.map(key => ({
                generic_name: key,
                specific_name: selectedAssessments[key]
            }))
        };

        setLoadingSave(true);
        try {
            const response = await makeApiRequest('post', '/api/course/update', payload);
            if (response.status === 'success') {
                toast.success('Course edit request submitted. Pending admin approval.');
                navigate('/course');
            }
        } catch (error) {
            console.error('Error updating course:', error);
            if (error.response?.status === 400) {
                setDuplicateError(error.response?.data?.message || 'A pending edit request already exists for this course.');
            } else if (error.response?.data?.errors?.missing_chapters) {
                setMissingChapters(error.response.data.errors.missing_chapters);
                toast.error('Some selected modules do not have any chapters. Please add at least one chapter to continue.');
            } else {
                toast.error(error.response?.data?.message || 'Failed to submit course edit request');
            }
        } finally {
            setLoadingSave(false);
        }
    };

    if (loadingData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" backgroundColor="#ECEDFF">
                <CircularProgress size={40} thickness={4} sx={{ color: '#481E82' }} />
            </Box>
        );
    }

    const selectedAssessmentCount = Object.keys(selectedAssessments).length;

    return (
        <Box sx={{ height: 'auto', minHeight: '100vh', backgroundColor: '#ECEDFF', pb: 5 }}>
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                closeOnClick
                pauseOnHover={false}
                theme="light"
            />
            <Grid container sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                <Grid item xs={11} md={8}>
                    <Box sx={{ height: '80px', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#481E82', fontWeight: '500', fontFamily: 'Roboto' }}>
                            Edit Course
                        </Typography>
                    </Box>

                    <Alert
                        severity="info"
                        icon={<InfoOutlinedIcon />}
                        sx={{ mb: 2, borderRadius: '8px' }}
                    >
                        Course edits are subject to admin approval. Submitting this form creates a pending request — the course will not change until an approver accepts it.
                    </Alert>

                    {duplicateError && (
                        <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                            {duplicateError}
                        </Alert>
                    )}

                    <Card sx={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)', p: 3 }}>
                        <CardContent>
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <TextField
                                    label="Course Name"
                                    required
                                    fullWidth
                                    value={courseName}
                                    onChange={(e) => { setCourseName(e.target.value); clearDuplicateError(); }}
                                    size="small"
                                />

                                <FormControl fullWidth size="small">
                                    <InputLabel id="modules-label">Select Modules</InputLabel>
                                    <Select
                                        labelId="modules-label"
                                        multiple
                                        value={selectedModuleIds}
                                        onChange={handleModuleChange}
                                        input={<OutlinedInput label="Select Modules" />}
                                        renderValue={(selected) => selected.map(id => modules.find(m => Number(m.module_id) === Number(id))?.module || id).join(', ')}
                                    >
                                        {modules.map((m) => (
                                            <MenuItem key={m.module_id} value={m.module_id}>
                                                <Checkbox checked={selectedModuleIds.indexOf(m.module_id) > -1 || selectedModuleIds.indexOf(Number(m.module_id)) > -1} />
                                                <ListItemText primary={m.module} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Divider />

                                <Box>
                                    <Typography variant="h6" sx={{ color: '#481E82', mb: 1, fontWeight: '500' }}>
                                        Link Assessments (Select 3 to 5) — {selectedAssessmentCount} selected *
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Check the box for each assessment and enter the specific onscreen name as it should appear in the system.
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {assessmentOptions.map((opt) => {
                                            const isSelected = selectedAssessments[opt.generic] !== undefined;
                                            return (
                                                <Grid item xs={12} key={opt.generic} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() => handleAssessmentCheckboxChange(opt.generic)}
                                                            />
                                                        }
                                                        label={opt.label}
                                                        sx={{ width: '250px' }}
                                                    />
                                                    {isSelected && (
                                                        <TextField
                                                            label={`Onscreen Name for ${opt.label}`}
                                                            required
                                                            size="small"
                                                            fullWidth
                                                            value={selectedAssessments[opt.generic]}
                                                            onChange={(e) => handleOnscreenNameChange(opt.generic, e.target.value)}
                                                        />
                                                    )}
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>

                                {missingChapters.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            The following modules must have at least one chapter before the course can be updated. Please add a chapter to resolve:
                                        </Alert>
                                        <Grid container spacing={2}>
                                            {missingChapters.map((m) => (
                                                <Grid item xs={12} key={m.module_id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Typography sx={{ width: '200px', fontWeight: 'bold' }}>{m.name}</Typography>
                                                    <TextField
                                                        placeholder="Chapter Name"
                                                        size="small"
                                                        value={newChapterNames[m.module_id] || ''}
                                                        onChange={(e) => setNewChapterNames(prev => ({ ...prev, [m.module_id]: e.target.value }))}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleAddChapterInline(m.module_id)}
                                                    >
                                                        Add Chapter
                                                    </Button>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}

                                <Box
                                    sx={{
                                        position: 'sticky',
                                        bottom: 0,
                                        backgroundColor: 'white',
                                        borderTop: '1px solid #E0E0E0',
                                        pt: 2,
                                        mt: 1,
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: 2,
                                    }}
                                >
                                    <Button variant="outlined" onClick={() => navigate('/course')}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={loadingSave || !!duplicateError}
                                        sx={{ backgroundColor: '#481E82', '&:hover': { backgroundColor: '#371566' } }}
                                    >
                                        {loadingSave ? 'Submitting...' : 'Submit for Approval'}
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default EditCourse;
